import {z} from 'zod'

import {mapStockSource, type StockSource, stockSourceRawFields} from 'src/equipment/stock-source'

const equipmentDeploymentStatusSchema = z.enum(['available', 'deployed', 'picked_up', 'dispatched'])

const equipmentCurrentStatusRawSchema = z.object({
    status: equipmentDeploymentStatusSchema,
    timestamp: z.string().nullable(),
    location_label: z.string().nullable(),
})

export const equipmentLocationEventRawSchema = z.object({
    id: z.string(),
    event_type: z.enum(['dispatched', 'deployed', 'picked_up', 'returned']),
    timestamp: z.string(),
    note: z.string().nullable(),
    project_id: z.string(),
    project_name: z.string(),
    location_label: z.string(),
})

export const equipmentLocationEventListRawSchema = z.object({
    events: z.array(equipmentLocationEventRawSchema),
    current_status: equipmentCurrentStatusRawSchema,
})

export type EquipmentLocationEventListRaw = z.infer<typeof equipmentLocationEventListRawSchema>

export type EquipmentDeploymentStatus = z.infer<typeof equipmentDeploymentStatusSchema>

export type EquipmentCurrentStatus = {
    status: EquipmentDeploymentStatus
    timestamp: string | null
    locationLabel: string | null
}

export type EquipmentLocationEvent = {
    id: string
    eventType: 'dispatched' | 'deployed' | 'picked_up' | 'returned'
    timestamp: string
    note: string | null
    projectId: string
    projectName: string
    locationLabel: string
}

export type EquipmentLocationEventList = {
    events: EquipmentLocationEvent[]
    currentStatus: EquipmentCurrentStatus
}

export const deployEquipmentRequestSchema = z.object({
    rentalAssetId: z.string(),
    jobLocationId: z.string(),
})

export type DeployEquipmentInput = z.input<typeof deployEquipmentRequestSchema>

export const deployEquipmentRequestRawSchema = deployEquipmentRequestSchema.transform(
    ({rentalAssetId, jobLocationId}) => ({
        rental_asset_id: rentalAssetId,
        job_location_id: jobLocationId,
    }),
)

export type DeployEquipmentRequestRaw = z.infer<typeof deployEquipmentRequestRawSchema>

export const driverEquipmentDeploymentRawSchema = z.object({
    id: z.string(),
    event_type: z.literal('deployed'),
    timestamp: z.string(),
    location_label: z.string(),
})

export type DriverEquipmentDeploymentRaw = z.infer<typeof driverEquipmentDeploymentRawSchema>

export type DriverEquipmentDeployment = {
    id: string
    eventType: 'deployed'
    timestamp: string
    locationLabel: string
}

export function mapEquipmentLocationEventList(
    raw: EquipmentLocationEventListRaw,
): EquipmentLocationEventList {
    return {
        events: raw.events.map((event) => ({
            id: event.id,
            eventType: event.event_type,
            timestamp: event.timestamp,
            note: event.note,
            projectId: event.project_id,
            projectName: event.project_name,
            locationLabel: event.location_label,
        })),
        currentStatus: {
            status: raw.current_status.status,
            timestamp: raw.current_status.timestamp,
            locationLabel: raw.current_status.location_label,
        },
    }
}

export function mapDriverEquipmentDeployment(
    raw: DriverEquipmentDeploymentRaw,
): DriverEquipmentDeployment {
    return {
        id: raw.id,
        eventType: raw.event_type,
        timestamp: raw.timestamp,
        locationLabel: raw.location_label,
    }
}

const driverStockDeliveredByLocationRawSchema = z.object({
    job_location_id: z.string(),
    location_name: z.string(),
    quantity: z.number(),
})

export const deployStockEquipmentRequestSchema = z.object({
    thirdPartyRentalAssetOfferingId: z.string().optional(),
    rentalAssetTypeId: z.string(),
    jobLocationId: z.string(),
    quantity: z.number().int().min(1),
})

export type DeployStockEquipmentInput = z.input<typeof deployStockEquipmentRequestSchema>

export const deployStockEquipmentRequestRawSchema = deployStockEquipmentRequestSchema.transform(
    ({rentalAssetTypeId, jobLocationId, quantity, thirdPartyRentalAssetOfferingId}) => ({
        ...(thirdPartyRentalAssetOfferingId
            ? {third_party_rental_asset_offering_id: thirdPartyRentalAssetOfferingId}
            : {}),
        rental_asset_type_id: rentalAssetTypeId,
        job_location_id: jobLocationId,
        quantity,
    }),
)

export type DeployStockEquipmentRequestRaw = z.infer<typeof deployStockEquipmentRequestRawSchema>

export const driverStockEquipmentDeploymentRawSchema = z.object({
    ...stockSourceRawFields,
    rental_asset_type_id: z.string(),
    rental_asset_type_name: z.string(),
    applied_quantity: z.number(),
    job_location_id: z.string(),
    location_label: z.string(),
    assigned_quantity: z.number(),
    delivered_quantity: z.number(),
    remaining_quantity: z.number(),
    delivered_by_location: z.array(driverStockDeliveredByLocationRawSchema),
})

export type DriverStockEquipmentDeploymentRaw = z.infer<
    typeof driverStockEquipmentDeploymentRawSchema
>

export type DriverStockEquipmentDeployment = StockSource & {
    rentalAssetTypeId: string
    rentalAssetTypeName: string
    appliedQuantity: number
    jobLocationId: string
    locationLabel: string
    assignedQuantity: number
    deliveredQuantity: number
    remainingQuantity: number
    deliveredByLocation: Array<{
        jobLocationId: string
        locationName: string
        quantity: number
    }>
}

export function mapDriverStockEquipmentDeployment(
    raw: DriverStockEquipmentDeploymentRaw,
): DriverStockEquipmentDeployment {
    return {
        rentalAssetTypeId: raw.rental_asset_type_id,
        rentalAssetTypeName: raw.rental_asset_type_name,
        appliedQuantity: raw.applied_quantity,
        ...mapStockSource(raw),
        jobLocationId: raw.job_location_id,
        locationLabel: raw.location_label,
        assignedQuantity: raw.assigned_quantity,
        deliveredQuantity: raw.delivered_quantity,
        remainingQuantity: raw.remaining_quantity,
        deliveredByLocation: raw.delivered_by_location.map((location) => ({
            jobLocationId: location.job_location_id,
            locationName: location.location_name,
            quantity: location.quantity,
        })),
    }
}
